
import { useState, useCallback } from 'react';

interface UseTouchZoomProps {
  zoom: number;
  setZoom: (zoom: number) => void;
  minZoom?: number;
  maxZoom?: number;
}

export const useTouchZoom = ({ zoom, setZoom, minZoom = 0.5, maxZoom = 3 }: UseTouchZoomProps) => {
  const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);

  const getTouchDistance = (touches: React.TouchList): number => {
    if (touches.length < 2) return 0;
    
    const touch1 = touches[0];
    const touch2 = touches[1];
    
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const distance = getTouchDistance(e.touches);
      setLastTouchDistance(distance);
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastTouchDistance !== null) {
      e.preventDefault();
      
      const currentDistance = getTouchDistance(e.touches);
      const scale = currentDistance / lastTouchDistance;
      
      const newZoom = Math.min(Math.max(zoom * scale, minZoom), maxZoom);
      setZoom(newZoom);
      setLastTouchDistance(currentDistance);
    }
  }, [zoom, lastTouchDistance, minZoom, maxZoom, setZoom]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      setLastTouchDistance(null);
    }
  }, []);

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  };
};
